/**
 * Zeitholz — Feature Chips + “Mehr” Drawer
 * Usage:
 *  - Add `data-zh-tabs` on the component root
 *  - “Mehr” button: `.zh-chip-mehr` with `aria-controls="<drawer-id>"` and `aria-expanded="false|true"`
 *  - Drawer container: `#<drawer-id>` with class `.zh-drawer` and `hidden` attribute when closed
 *  - Close button inside drawer: `.zh-drawer__close`
 *  - Optional: drawer open class `.is-open` (used for animation)
 */

(() => {
  const SELECTORS = {
    root: '[data-zh-tabs]',
    moreBtn: '.zh-chip-mehr',
    drawer: '.zh-drawer',
    closeBtn: '.zh-drawer__close',
    openClass: 'is-open',
  };

  const TRANSITION_MS = 260; // must match CSS transition duration for smooth hidden toggling

  /**
   * Create one instance controller for a single feature-fork component.
   */
  function createInstance(root) {
    const moreBtn = root.querySelector(SELECTORS.moreBtn);
    if (!moreBtn) return null;

    const drawerId = moreBtn.getAttribute('aria-controls');
    const drawer = drawerId ? document.getElementById(drawerId) : null;
    if (!drawer) return null;

    const closeBtn = drawer.querySelector(SELECTORS.closeBtn);

    const isOpen = () => moreBtn.getAttribute('aria-expanded') === 'true';

    const open = () => {
      // Make it renderable
      drawer.hidden = false;
      // Trigger transition on next frame
      requestAnimationFrame(() => drawer.classList.add(SELECTORS.openClass));
      moreBtn.setAttribute('aria-expanded', 'true');
    };

    const close = () => {
      drawer.classList.remove(SELECTORS.openClass);
      moreBtn.setAttribute('aria-expanded', 'false');

      // Wait until transition completes, then hide
      window.setTimeout(() => {
        if (!drawer.classList.contains(SELECTORS.openClass)) {
          drawer.hidden = true;
        }
      }, TRANSITION_MS);
    };

    const toggle = () => (isOpen() ? close() : open());

    // Instance-level handlers
    moreBtn.addEventListener('click', toggle);
    if (closeBtn) closeBtn.addEventListener('click', close);

    return { root, moreBtn, drawer, isOpen, open, close };
  }

  function init() {
    const roots = Array.from(document.querySelectorAll(SELECTORS.root));
    if (!roots.length) return;

    const instances = roots
      .map((root) => createInstance(root))
      .filter(Boolean);

    if (!instances.length) return;

    // One global Escape handler for all instances
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      instances.forEach((i) => {
        if (i.isOpen()) i.close();
      });
    });

    // One global outside-click handler for all instances
    document.addEventListener('click', (e) => {
      instances.forEach((i) => {
        if (!i.isOpen()) return;

        const clickedInside = i.drawer.contains(e.target) || i.moreBtn.contains(e.target);
        if (!clickedInside) i.close();
      });
    });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
